import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { ScrollText, User, FileText, Receipt } from "lucide-react";

const ENTITY_LABELS = {
  tenant: "Mandant", account: "Konto", customer: "Kunde", project: "Projekt",
  outgoing_invoice: "Ausgangsrechnung", incoming_invoice: "Eingangsrechnung",
  customer_payment: "Kundenzahlung", fixed_cost: "Fixkosten", inbox: "Inbox",
  letterhead: "Briefkopf", logo: "Logo", payment_model: "Zahlungsmodell",
  invite: "Einladung", user: "Benutzer", email_settings: "E-Mail-Einstellungen",
};
const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  upload: "bg-violet-100 text-violet-700",
  accept_inbox: "bg-amber-100 text-amber-700",
  pay: "bg-cyan-100 text-cyan-700",
  send_dunning: "bg-orange-100 text-orange-700",
  generate_invoices: "bg-fuchsia-100 text-fuchsia-700",
  update_role: "bg-yellow-100 text-yellow-700",
  remove: "bg-red-100 text-red-700",
};

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/audit-log/full")
      .then(r => setItems(r.data))
      .catch(e => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);

  if (denied) return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
      Diese Seite ist nur für Admins und den Inhaber sichtbar.
    </div>
  );

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Audit</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
          <ScrollText className="h-7 w-7" /> Aktivitätsverlauf
        </h1>
        <p className="text-sm text-neutral-500 mt-2">Wer hat wann was geändert. Lückenlose Nachvollziehbarkeit für Mandanten und Steuerberater.</p>
      </div>

      {loading ? <div className="text-neutral-500">Lade…</div> :
        items.length === 0 ? <div className="rounded-xl border border-dashed p-12 text-center text-neutral-500">Noch keine Aktivitäten</div> : (
          <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid="audit-list">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Zeitpunkt</th>
                  <th className="text-left px-5 py-3 font-semibold">Benutzer</th>
                  <th className="text-left px-5 py-3 font-semibold">Aktion</th>
                  <th className="text-left px-5 py-3 font-semibold">Bereich</th>
                  <th className="text-left px-5 py-3 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a, i) => (
                  <tr key={a.id || i} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`audit-row-${i}`}>
                    <td className="px-5 py-3 text-neutral-700 text-xs whitespace-nowrap">{new Date(a.timestamp).toLocaleString("de-DE")}</td>
                    <td className="px-5 py-3">
                      <div className="font-medium">{a.user_name || "—"}</div>
                      <div className="text-xs text-neutral-500">{a.user_email}</div>
                    </td>
                    <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-[11px] uppercase tracking-wider font-semibold ${ACTION_COLORS[a.action] || "bg-neutral-100 text-neutral-700"}`}>{a.action}</span></td>
                    <td className="px-5 py-3 text-neutral-700">{ENTITY_LABELS[a.entity] || a.entity}</td>
                    <td className="px-5 py-3 text-neutral-500 text-xs">
                      {a.entity_id && <div className="font-mono">{a.entity_id.length > 32 ? a.entity_id.slice(0, 8) + "…" : a.entity_id}</div>}
                      {a.details && Object.keys(a.details).length > 0 && <div className="mt-0.5">{JSON.stringify(a.details).slice(0, 80)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}
